import React, { useContext, useEffect, useState } from 'react';
import { FormContext, FormSchemaFields } from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { FormModal } from '../../Shared/form';
import { ProjectContext } from '../../../contexts/ProjectContext';
import schedulerApi from '../../../utils/api/inspectionSchedular';
import inspectionApi from '../../../utils/api/inspection';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import {
  useInspectionTypesByTemplate,
  defaultInspectionTypeOptions,
} from '../../../hooks/inspectionTypes';

interface AssignedForm {
  name_of_inspection: any;
  junction_id: number;
  template_id: number;
  template_name: string;
  inspection_types: string;
  inspector_id: number | null;
  inspector_name: string | null;
  active: boolean;
}

interface AssignedFormInspectionModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: () => void;
  projectId?: number;
  /**
   * SW2-2334 — escape hatch to the One Time flow, for a form that is not assigned to
   * this project. Secondary on purpose: 54% of One Time use in the last 90 days was on
   * a form the project already had assigned, i.e. this modal would have done the job.
   */
  onUseOneTime?: () => void;
}

const AssignedFormInspectionModal: React.FC<
  AssignedFormInspectionModalProps
> = ({ show, onClose, onSubmit, projectId, onUseOneTime }) => {
  const formContext = useContext(FormContext);
  const projectStore = useContext(ProjectContext);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [assignedForms, setAssignedForms] = useState<AssignedForm[]>([]);
  const [selectedForm, setSelectedForm] = useState<AssignedForm | null>(null);

  // Fetch inspection types based on selected form's template_id
  const { data: inspectionTypesData, isLoading: isLoadingInspectionTypes } =
    useInspectionTypesByTemplate(
      selectedForm?.template_id,
      show && !!selectedForm?.template_id,
    );

  // Fetch assigned forms from API
  const fetchAssignedForms = async () => {
    const currentProjectId = projectId || projectStore.project?.id;

    if (!currentProjectId) {
      console.log('Missing project ID - not making API call');

      return;
    }

    setIsLoading(true);
    try {
      const response = await schedulerApi.getProjectForms(currentProjectId);

      if (response.success) {
        // Filter only active forms
        const activeForms = response.data.filter(
          (form: AssignedForm) => form.active,
        );

        setAssignedForms(activeForms);

        if (activeForms.length === 0) {
          toast.warning('No active assigned forms found for this project');
        }

        // SW2-2334 — with exactly one assigned form there is no choice to make, so
        // make it. Set through the same path a manual pick takes, otherwise the
        // dependent Inspection Type list never loads and the field stays disabled on
        // "Select an assigned form first".
        if (activeForms.length === 1) {
          const only = activeForms[0];

          formContext.set('assigned_form', only.junction_id.toString());
          setSelectedForm(only);

          if (only.inspector_id) {
            formContext.set('inspector_id', only.inspector_id.toString());
          }
        }
      } else {
        console.error('API call failed:', response);
        toast.error('Failed to load assigned forms');
      }
    } catch (error) {
      console.error('Error fetching assigned forms:', error);
      toast.error('Error loading assigned forms');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchAssignedForms();
    } else {
      // Reset state when modal is closed
      setSelectedForm(null);
      setAssignedForms([]);
    }
  }, [show, projectId, projectStore.project?.id]);

  // Handle form selection change
  const handleFormChange = (junctionId: string) => {
    const form = assignedForms.find(
      (f) => f.junction_id.toString() === junctionId,
    );

    setSelectedForm(form || null);

    // Clear inspection type when form changes since inspection types depend on template
    formContext.set('inspection_type', '');

    // Auto-populate inspector if available
    if (form && form.inspector_id) {
      formContext.set('inspector_id', form.inspector_id.toString());
    }
  };

  const handleSubmit = async () => {
    const formData = formContext.value;
    const currentProjectId = projectId || projectStore.project?.id;

    if (!currentProjectId) {
      toast.error('Project ID is required');

      return;
    }

    if (!formData.assigned_form) {
      toast.error('Please select an assigned form');

      return;
    }

    if (!formData.inspection_type) {
      toast.error('Please select an inspection type');

      return;
    }

    if (!formData.inspection_date) {
      toast.error('Please choose an inspection date');

      return;
    }

    const today = new Date();

    today.setHours(0, 0, 0, 0);

    // The date control emits MM-dd-yyyy (see lib-form's DatePicker wrapper), NOT the
    // ISO shape. Built as local midnight rather than `new Date(str)`, which would read
    // an ISO string as UTC and land a day early for anyone behind it.
    const [mm, dd, yyyy] = String(formData.inspection_date).split('-').map(Number);
    const chosen = new Date(yyyy, (mm || 1) - 1, dd || 1);

    if (Number.isNaN(chosen.getTime())) {
      toast.error('Please choose a valid inspection date');

      return;
    }

    if (chosen < today) {
      toast.error('Inspection date cannot be in the past');

      return;
    }

    const isFuture = chosen > today;
    // Sent as ISO so the API is not relying on V8's lenient parsing of MM-dd-yyyy.
    const isoDate = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;

    try {
      setIsLoading(true);

      const selectedFormData = assignedForms.find(
        (f) => f.junction_id.toString() === formData.assigned_form,
      );

      if (!selectedFormData) {
        toast.error('Selected form not found');

        return;
      }

      // SW2-2334: a future date sets the form's next due date instead of creating
      // anything now. The daily creation cron picks it up on the day, exactly as it
      // does for a recurring form — the difference for On Demand is only that nothing
      // auto-schedules the one after it.
      if (isFuture) {
        // This endpoint answers with { success, message } and resolves even when it
        // failed, so the payload has to be checked. Without it a handled failure would
        // still toast success, close the modal and refresh — telling the user an
        // inspection is scheduled when nothing was written. The Upcoming due-date editor
        // already checks it; this path was not.
        const response = await schedulerApi.updateNextInspectionDate(
          selectedFormData.junction_id,
          { next_inspection_date: isoDate },
        );

        if (!response?.success) {
          toast.error(
            response?.message || 'Failed to schedule the inspection. Please try again.',
          );

          return;
        }

        toast.success(`Inspection scheduled for ${formData.inspection_date}`);
        onSubmit();
        onClose();

        return;
      }

      // Use the same API as "Start Inspection" - POST to /api/inspections
      const requestBody = {
        projectId: currentProjectId,
        inspection_types: [formData.inspection_type],
        junctionId: selectedFormData.junction_id,
      };

      console.log('Creating inspection with:', requestBody);

      const data = await inspectionApi.createWithJunction(requestBody);

      if (data && data.id) {
        toast.success('Inspection created successfully');
        onSubmit();
        onClose();

        // Navigate to the newly created inspection's questionnaire
        history.push(`/inspection/${data.id}/questionnaire`);
      } else {
        console.error('Failed to create inspection: No inspection ID returned');
        toast.error('Failed to create inspection');
      }
    } catch (error) {
      console.error('Error creating inspection:', error);
      const axiosError = error as any;

      if (
        axiosError?.response?.status === 403 &&
        axiosError?.response?.data?.error?.includes('Authorization & Agreement')
      ) {
        toast.error(
          'The Authorization & Agreement must be signed by both parties before inspections can be started.',
        );
      } else {
        toast.error('Failed to create inspection. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Convert assigned forms to dropdown options
  const assignedFormOptions = assignedForms.map((form) => ({
    value: form.junction_id.toString(),
    label: `${form.name_of_inspection}`,
  }));

  // Custom onChange handler
  const handleFormFieldChange = (fieldName: string, value: any) => {
    if (fieldName === 'assigned_form') {
      handleFormChange(value);
    }
    formContext.set(fieldName, value);
  };

  // Build inspection type options from API data, merging with defaults and removing duplicates
  const inspectionTypeOptions = (() => {
    const templateTypes = Array.isArray(inspectionTypesData?.inspectionTypes)
      ? inspectionTypesData.inspectionTypes
      : [];

    // Merge template types with defaults, removing duplicates
    const allTypes = Array.from(
      new Set([...templateTypes, ...defaultInspectionTypeOptions]),
    );

    return allTypes.map((type: string, index: number) => ({
      id: index + 1,
      value: type,
    }));
  })();

  // Determine placeholder text for inspection type field
  const getInspectionTypePlaceholder = () => {
    if (!selectedForm) {
      return 'Select an assigned form first';
    }
    if (isLoadingInspectionTypes) {
      return 'Loading inspection types...';
    }
    if (inspectionTypeOptions.length === 0) {
      return 'No inspection types available';
    }

    return 'Select inspection type';
  };

  const schema = {
    assigned_form: {
      label: 'Assigned Form*',
      controlType: UIControlType.select,
      options: assignedFormOptions,
      validation: { required: true },
      placeholder: isLoading
        ? 'Loading assigned forms...'
        : 'Select an assigned form',
      disabled: isLoading || assignedForms.length === 0,
    },
    inspection_type: {
      label: 'Inspection Type*',
      controlType: UIControlType.select,
      validation: { required: true },
      options: inspectionTypeOptions,
      labelKey: 'value',
      valueKey: 'value',
      placeholder: getInspectionTypePlaceholder(),
      disabled:
        !selectedForm ||
        isLoadingInspectionTypes ||
        inspectionTypeOptions.length === 0,
    },
    inspection_date: {
      label: 'Inspection Date*',
      controlType: UIControlType.date,
      validation: { required: true },
      // SW2-2334: the point of this field. Leaving it on today keeps the old
      // behaviour (create it now and open it); a future date schedules the form
      // instead, which is the only way to plan ahead on an assigned form and the
      // only way to use an On Demand interval at all.
      //
      // The control spreads unknown props into react-datepicker, so month/year
      // dropdowns and a Today shortcut come for free — scheduling is usually weeks
      // out, and paging month by month with the default arrows is tedious.
      // minDate stops a past date being pickable at all rather than only rejecting
      // it on submit.
      showMonthDropdown: true,
      showYearDropdown: true,
      dropdownMode: 'select',
      todayButton: 'Today',
      minDate: new Date(),
      description:
        'Today creates the inspection now. A future date schedules it — it will be created on that day.',
    },
    inspector_name: {
      label: 'Inspector',
      controlType: UIControlType.text,
      placeholder:
        selectedForm?.inspector_name || 'Inspector from assigned form',
      disabled: true,
      description: 'Inspector is automatically assigned from the selected form',
    },
  };

  if (!show) return null;

  return (
    <FormModal
      modalProps={{
        title: 'New Inspection - Assigned Form',
        classes: 'assigned-form-inspection-modal',
      }}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText='Create Inspection'
    >
      <div className='assigned-form-inspection-modal-content'>
        {isLoading ? (
          <div className='loading-state'>
            <p>Loading assigned forms...</p>
          </div>
        ) : assignedForms.length === 0 ? (
          <div className='no-forms-state'>
            <p>No active assigned forms found for this project.</p>
            <p
              style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}
            >
              Please add and activate forms in the Inspection Settings section
              first.
            </p>
          </div>
        ) : (
          <FormSchemaFields
            schema={schema}
            formData={formContext.value}
            onChange={handleFormFieldChange}
          />
        )}
        {onUseOneTime && (
          <p className='one-time-escape-hatch'>
            Need a form that is not assigned to this project?{' '}
            <button type='button' onClick={onUseOneTime}>
              Use a one-time inspection
            </button>
          </p>
        )}
      </div>
    </FormModal>
  );
};

export default AssignedFormInspectionModal;
